<script setup lang="ts">
import type {Address} from "~/composables/store/user/address/interface";
import type {PropType} from "vue";
import AddressRow from "~/components/component/Address/AddressRow.vue";
import BackDrop from "~/components/modal/BackDrop.vue";
import DownUpModal from "~/components/modal/DownUpModal.vue";


const props = defineProps({
  addresses: {
    type: Array as PropType<Address[]>,
    default: () => []
  },
  selectedAddress: {
    type: Object as PropType<Address|null>,
    default: () => null
  }
})
const { isMobile } = useDevice()
const route = useRoute()
const isBccPage = computed(() => route.name === 'bcc')
const selectedModel = ref(props.selectedAddress)
const emit = defineEmits(['handleEdit','handleDelete','handleSelect'])
const openMobileAddressControls = ref(false)

function cardClass(address: Address) {
  const selected = address.address_id === selectedModel.value?.address_id
  if (isBccPage.value) {
    return selected
      ? 'border-[1.5px] rounded-[16px] bg-primary-green bg-opacity-10 border-primary-green'
      : 'border-[1.5px] rounded-[16px] bg-white border-[#E8EAE8]'
  }
  return selected
    ? 'rounded-2xl border bg-primary-green-50 border-primary-green'
    : 'rounded-2xl border bg-secondary border-secondary-400'
}

function handleSelectAddress(address: Address) {
  emit('handleSelect',address)
}
function handleDelete(address_id:string){
  emit('handleDelete',address_id)
  if (selectedModel.value?.address_id===address_id){
    selectedModel.value=null
  }
  openMobileAddressControls.value = false
}

watch(()=>props.selectedAddress,(value)=>{
  selectedModel.value=value
})

function handleEditAddress(address:Address)
{
  emit('handleEdit',address)
}

</script>

<template>
  <!-- В мини-аппе прокручивается вся оболочка (layouts/bcc.vue), поэтому свою прокрутку
       списку не даём: при 5+ адресах она обрезала список, а кнопка «Добавить адрес»
       вставала сразу под обрезанной областью -->
  <div class="flex flex-col gap-2" :class="{'max-h-[300px] overflow-y-auto small-scrollbar': !isBccPage}">
    <template v-for="(address, index) in addresses">
      <label
          :for="`address-${address?.address_id}`"
          :class="cardClass(address)"
          class="flex flex-row items-center gap-2.5 md:p-2.5 p-2 cursor-pointer"
      >
        <div class="flex w-full items-center gap-2.5">
          <input :id="`address-${address?.address_id}`"
                 v-model="selectedModel"
                 type="radio" name="user-address"
                 class="hidden"
                 :value="address" @change="handleSelectAddress(address)">
          <AddressRow
              v-if="address"
              :address="address"
          />
        </div>
        <button
            v-if="isMobile"
            @click="selectedModel = address; openMobileAddressControls = true"
            class="w-8 h-8 flex items-center justify-center"
        >
          <IconUIMore/>
        </button>
        <div v-else-if="isBccPage" class="ml-auto flex gap-2 flex-shrink-0">
          <button
              type="button"
              @click="handleEditAddress(address)"
              class="w-9 h-9 rounded-full border-[1.5px] border-primary-green flex items-center justify-center text-primary-green"
          >
            <IconUIEdit class="w-4 h-4"/>
          </button>
          <button
              type="button"
              @click="handleDelete(address.address_id)"
              class="w-9 h-9 rounded-full border-[1.5px] border-primary-red flex items-center justify-center text-primary-red"
          >
            <IconUIBin class="w-4 h-4"/>
          </button>
        </div>
        <div v-else class="ml-auto grid grid-cols-2 md:w-fit w-full md:ml-auto md:gap-6 gap-3">
          <UIBtn
              :border="'gray'"
              :color="'green'"
              class="md:w-[40px] h-[40px] p-0 rounded-xl flex items-center justify-center"
              @click="handleEditAddress(address)"
          >
            <span v-if="isMobile">{{ $t('input_forms.button.edit') }}</span>
            <IconUIEdit v-else/>
          </UIBtn>
          <UIBtn
              :color="'red'"
              class="md:w-[40px] h-[40px] p-0 rounded-xl flex items-center justify-center"
              @click="handleDelete(address.address_id)"
          >
            <span v-if="isMobile">{{ $t('input_forms.button.delete') }}</span>
            <IconUIBin v-else class="h-6 w-5 fill-white"/>
          </UIBtn>
        </div>
      </label>
    </template>

    <BackDrop @close-modal="openMobileAddressControls = false" :open-modal="openMobileAddressControls"/>
    <DownUpModal :open-modal="openMobileAddressControls">
      <div v-if="isBccPage" class="p-5 flex flex-col gap-3">
        <p class="text-[15px] font-bold text-[#16211C] mb-1">
          <AddressRow
              v-if="selectedModel"
              :address="selectedModel"
          />
        </p>

        <button
            type="button"
            @click="handleEditAddress(selectedModel)"
            class="h-[50px] rounded-2xl border-[1.5px] border-primary-green bg-white text-primary-green text-[14px] font-bold flex items-center justify-center gap-2"
        >
          <IconUIEdit class="w-4 h-4"/>
          {{ $t('input_forms.button.edit') }}
        </button>
        <button
            type="button"
            @click="handleDelete(selectedModel?.address_id)"
            class="h-[50px] rounded-2xl border-[1.5px] border-primary-red bg-white text-primary-red text-[14px] font-bold flex items-center justify-center gap-2"
        >
          <IconUIBin class="w-4 h-4"/>
          {{ $t('input_forms.button.delete') }}
        </button>
      </div>
      <div v-else class="p-2.5 py-4 flex flex-col gap-3">
        <p class="text-base font-bold">
          <AddressRow
              v-if="selectedModel"
              :address="selectedModel"
          />
        </p>

        <UIBtn @click="handleEditAddress(selectedModel)" class="flex bg-secondary-300 items-center gap-1 text-primary-blue-900 p-3 font-medium text-sm rounded-xl">
          <IconUIEdit class="text-primary-green"/>
          {{ $t('input_forms.button.edit') }}
        </UIBtn>
        <UIBtn @click="handleDelete(selectedModel?.address_id)" class="flex bg-secondary-300 items-center gap-1 text-primary-blue-900 p-3 font-medium text-sm rounded-xl">
          <IconUIBin class="text-primary-green"/>
          {{ $t('input_forms.button.delete') }}
        </UIBtn>
      </div>
    </DownUpModal>
  </div>
</template>

<style scoped>

</style>